import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  RetmIntegrationStatus,
  RetmOrdersResponse,
  RetmBranchesResponse,
  RetmBranchMapping,
  RetmBranchMappingsResponse,
  RetmCreateBranchMappingBody,
  RetmMenuEntitiesResponse,
  ImportRetmMenuEntitiesBody,
  ImportRetmOrdersBody,
  ImportRetmOrdersResponse,
} from "src/types/retm";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

// API Functions //

const getRetmStatus = async (): Promise<RetmIntegrationStatus> => {
  return apiClient.get("/api/retm/status");
};

const disconnectRetm = async (): Promise<{ message: string }> => {
  return apiClient.post("/api/retm/disconnect");
};

const getRetmOrders = async (params?: {
  source?: "local" | "api";
  sync?: boolean;
  status?: number;
  limit?: number;
}): Promise<RetmOrdersResponse> => {
  return apiClient.get("/api/retm/orders", { params });
};

const syncRetmOrders = async (): Promise<{ message: string; synced_count: number }> => {
  return apiClient.post("/api/retm/orders/sync");
};

const getRetmBranches = async (): Promise<RetmBranchesResponse> => {
  return apiClient.get("/api/retm/branches");
};

const getRetmBranchMappings = async (): Promise<RetmBranchMappingsResponse> => {
  return apiClient.get("/api/retm/mappings/branches");
};

const createRetmBranchMapping = async (
  data: RetmCreateBranchMappingBody
): Promise<{ message: string; data: RetmBranchMapping }> => {
  return apiClient.post("/api/retm/mappings/branches", data);
};

const deleteRetmBranchMapping = async (
  id: number
): Promise<{ message: string }> => {
  return apiClient.delete(`/api/retm/mappings/branches/${id}`);
};

const getRetmMenuEntities = async (params?: {
  products_page?: number;
  products_per_page?: number;
  categories_page?: number;
  categories_per_page?: number;
}): Promise<RetmMenuEntitiesResponse> => {
  return apiClient.get("/api/retm/menu/entities", { params });
};

const importRetmMenuEntities = async (
  data: ImportRetmMenuEntitiesBody
): Promise<{ message: string; data: { imported_categories: number; imported_products: number } }> => {
  return apiClient.post("/api/retm/menu/import", data);
};

const importRetmOrders = async (
  data: ImportRetmOrdersBody
): Promise<ImportRetmOrdersResponse> => {
  return apiClient.post("/api/retm/orders/import", data);
};

// React Query Hooks //

export const useRetmStatus = () =>
  useQuery<RetmIntegrationStatus, APIErrorResponse>({
    queryKey: ["retm-status"],
    queryFn: getRetmStatus,
  });

export const useDisconnectRetm = () =>
  useMutation<{ message: string }, APIErrorResponse, void>({
    mutationFn: disconnectRetm,
  });

export const useRetmOrders = (params?: {
  source?: "local" | "api";
  sync?: boolean;
  status?: number;
  limit?: number;
}) =>
  useQuery<RetmOrdersResponse, APIErrorResponse>({
    queryKey: ["retm-orders", params],
    queryFn: () => getRetmOrders(params),
  });

export const useSyncRetmOrders = () =>
  useMutation<{ message: string; synced_count: number }, APIErrorResponse, void>({
    mutationFn: syncRetmOrders,
  });

export const useRetmBranches = (enabled: boolean = true) =>
  useQuery<RetmBranchesResponse, APIErrorResponse>({
    queryKey: ["retm-branches"],
    queryFn: getRetmBranches,
    enabled,
  });

export const useRetmBranchMappings = (enabled: boolean = true) =>
  useQuery<RetmBranchMappingsResponse, APIErrorResponse>({
    queryKey: ["retm-branch-mappings"],
    queryFn: getRetmBranchMappings,
    enabled,
  });

export const useRetmCreateBranchMapping = () =>
  useMutation<
    { message: string; data: RetmBranchMapping },
    APIErrorResponse,
    RetmCreateBranchMappingBody
  >({
    mutationFn: createRetmBranchMapping,
  });

export const useRetmDeleteBranchMapping = () =>
  useMutation<{ message: string }, APIErrorResponse, number>({
    mutationFn: deleteRetmBranchMapping,
  });

export const useRetmMenuEntities = (
  params?: {
    products_page?: number;
    products_per_page?: number;
    categories_page?: number;
    categories_per_page?: number;
  },
  enabled: boolean = true
) =>
  useQuery<RetmMenuEntitiesResponse, APIErrorResponse>({
    queryKey: ["retm-menu-entities", params],
    queryFn: () => getRetmMenuEntities(params),
    enabled,
  });

export const useImportRetmMenuEntities = () =>
  useMutation<
    { message: string; data: { imported_categories: number; imported_products: number } },
    APIErrorResponse,
    ImportRetmMenuEntitiesBody
  >({
    mutationFn: importRetmMenuEntities,
  });

export const useImportRetmOrders = () =>
  useMutation<ImportRetmOrdersResponse, APIErrorResponse, ImportRetmOrdersBody>({
    mutationFn: importRetmOrders,
  });
